<script lang="ts" setup>
import type { CasinoGameItemExtend } from '~/types/game'
import { IMAGE_GAME_ITEM_DEFAULT } from '~/constants'
import { CasinoPlayerType } from '~/enums/game'
import { STATUS_LABEL, LIVE_STREAM_PROVIDER } from '~/resources'
import { useCustomFavoriteGame } from '~/composables/account/use-custom-favorite-game'
import { useGameSpecificEvents } from '~/composables/tracking-events/use-game-specific-events'
import { useUserBehaviorEvents } from '~/composables/tracking-events/use-user-behavior-events'
import { useLivestreamGame } from '~/composables/game/use-livestream-game'

const props = defineProps({
  item: {
    type: Object as () => CasinoGameItemExtend,
    required: true,
  },
  casinoGameLimit: {
    type: Number,
    default: 10,
  },
  customClass: {
    type: String,
    default: '',
  },
  isComingSoon: {
    type: Boolean,
    default: false,
  },
})
const isLoading = ref(true)
const route = useRoute()
const { isLogged } = useUser()
const { trackingButtonClick } = useUserBehaviorEvents()
const { trackingGameStarted } = useGameSpecificEvents()
const { isMobile } = useDevice()
const { connectMillicastVideo, isShowError, initNanoPlayer } =
  useLivestreamGame({
    game: { ...props.item, width: '432px', height: '259px' },
  })

const { isCheckFavorite, handleFavoriteGames } = useCustomFavoriteGame()
const { playCasinoGame } = useCasino({
  gameLimit: props.casinoGameLimit,
})

const handlePlayingGame = (item: CasinoGameItemExtend): void => {
  trackingButtonClick({
    buttonId: `play-game-id-${item.gameId}`,
    buttonName: 'play-game-home',
    pageUrl: route.fullPath,
  })

  if (props.isComingSoon) {
    return
  }

  playCasinoGame({ ...item, newTab: true } as CasinoGameItemExtend)

  if (isLogged.value) {
    trackingGameStarted({
      gameId: item.gameId,
      gameType: item.category?.[0] ?? item.gameType ?? '',
      gameName: item.title,
    })
  }
}

onMounted(() => {
  setTimeout(async () => {
    if (props.item?.playerType === CasinoPlayerType.Millicast) {
      await connectMillicastVideo()
    } else {
      await initNanoPlayer()
    }
  }, 100)
})
</script>
<template>
  <div
    :class="[
      customClass,
      'livestream-item',
      { 'livestream-item--mobile': isMobile },
    ]"
  >
    <div class="livestream-item__image" @click="handlePlayingGame(item)">
      <div class="ratio">
        <BaseImage
          v-show="isLoading || isShowError(item.key ?? '')"
          :src="item?.img?.banner?.url || item?.img?.thumbnail?.url || ''"
          :image-default="IMAGE_GAME_ITEM_DEFAULT"
          :alt="item?.title"
        />
        <ClientOnly>
          <CommonVideoLivestream
            :id="`player-${item.key}`"
            :is-error="isShowError(item.key ?? '')"
            :player-type="item?.playerType"
          />
        </ClientOnly>
      </div>
      <div v-if="!isMobile" class="livestream-item__thumb">
        <div class="livestream-item__play">
          <span class="icon-flash" />
          Chơi ngay
        </div>
      </div>
    </div>
    <div class="livestream-item__footer" @click="handlePlayingGame(item)">
      <div class="livestream-item__footer-left">
        <div v-if="item.title" class="livestream-item__game-name line-limit-1">
          {{ item.title }}
        </div>
        <div v-if="item.range" class="livestream-item__range">
          <span class="livestream-item__range-text line-limit-1">{{
            item?.range
          }}</span>
        </div>
      </div>
      <div
        v-if="
          item?.provider &&
          LIVE_STREAM_PROVIDER[item?.provider.toLocaleLowerCase() ?? '']
        "
        class="livestream-item__footer-right"
      >
        <img
          :src="
            LIVE_STREAM_PROVIDER[item?.provider.toLocaleLowerCase() ?? '']?.logo
          "
          class="logo-provider"
          :alt="
            LIVE_STREAM_PROVIDER[item?.provider.toLocaleLowerCase() ?? '']
              ?.provider
          "
          loading="lazy"
        />
      </div>
    </div>
    <template v-for="(status, key) in STATUS_LABEL">
      <div
        v-if="item.group?.includes(status.value)"
        :key="key"
        class="livestream-item__badge"
      >
        <div
          :class="[
            'livestream-item__status-wrapper',
            `livestream-item__badge--${status.value}`,
          ]"
        >
          <span v-if="status.icon" :class="status.icon" />
          <span v-if="status.label" class="livestream-item__label">{{
            status.label
          }}</span>
        </div>
      </div>
    </template>
    <div
      :class="[
        'livestream-item__favorite',
        {
          'livestream-item__favorite--active':
            isCheckFavorite(item) || item?.favorite,
        },
        { 'livestream-item__favorite--mobile': isMobile },
      ]"
      @click="handleFavoriteGames(item)"
    />
  </div>
</template>
<style scoped lang="scss">
@import '~/assets/styles/components/common/livestream-item.scss';
</style>
